import { DocumentNode, gql, useApolloClient } from "@apollo/client";
import { Button, Flex, Text, Tooltip } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { RiLinkUnlink } from "react-icons/ri";
import { CopyToClipboard } from "src/components/Text/CopyToClipboard";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { isNonEmptyString } from "src/services/predicates";
import * as GQL from "src/types/graphql";

type Props = {
  personId: uuid;
  userId: string | undefined;
  refetchQueriesAfterDelink: (DocumentNode | string)[];
};

// only avela admin can use this mechanism at the moment.
// no other role has permission to update user_id on a person row.
export const DelinkUser: React.FC<Props> = ({
  personId,
  userId,
  refetchQueriesAfterDelink,
}) => {
  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "De-link user",
    body: "Are you sure you want to de-link this user?",
    confirmButton: { label: "Yes, de-link user" },
  });
  const client = useApolloClient();
  const toast = useAvelaToast();
  const [delinkingUser, setDelinkingUser] = useState(false);
  const delinkUser = useCallback(async () => {
    if (!(await confirm())) {
      return;
    }

    try {
      setDelinkingUser(true);
      const response = await client.mutate<
        GQL.DelinkUser,
        GQL.DelinkUserVariables
      >({
        mutation: gql`
          mutation DelinkUser($personId: uuid!) {
            update_person_by_pk(
              pk_columns: { id: $personId }
              _set: { user_id: null }
            ) {
              id
              user_id
            }
          }
        `,
        variables: {
          personId,
        },
        refetchQueries: refetchQueriesAfterDelink,
        awaitRefetchQueries: true,
      });

      if (response.errors) {
        console.error(response.errors);
        toast.error({
          title: "Error de-linking user",
        });
      }
    } catch (error) {
      console.error(error);
      toast.error({
        title: "Error de-linking user",
      });
    } finally {
      setDelinkingUser(false);
    }
  }, [client, confirm, personId, refetchQueriesAfterDelink, toast]);
  return (
    <Flex alignItems="center" gap="4">
      <Flex gap="2" direction="column">
        <Text>Auth0 ID</Text>
        {isNonEmptyString(userId) ? <CopyToClipboard text={userId} /> : null}
        {userId ? (
          <Tooltip label="De-link Auth0 ID">
            <Button
              isLoading={delinkingUser}
              leftIcon={<RiLinkUnlink />}
              variant="outline"
              colorScheme="gray"
              onClick={delinkUser}
              alignSelf="flex-start"
            >
              Unlink Auth0 ID
            </Button>
          </Tooltip>
        ) : null}
      </Flex>
      {confirmationDialog}
    </Flex>
  );
};
